/**
 * MescreenOrderModal.tsx
 *
 * Thin LabOrderModalCore adapter for mescreen (Versea Health) — an at-home
 * mitochondrial-health test kit mailed to the patient. There is one product:
 * the kit. The server creates the order on mescreen's Shopify store; the kit's
 * status, barcode and results are then polled from mescreen.
 */
import { useMemo, useRef } from 'react';

import {
  createIntegrationOrder,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
} from '@/services/integrations.service';
import { updatePatient } from '@/services/patients.service';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult } from './LabOrderModalCore';

interface MescreenOrderModalProps {
  patientId: number;
  integration: EnabledIntegration;
  isOpen: boolean;
  onClose: () => void;
  onOrdered: (result: CreateIntegrationOrderResult, integrationProductId: number | null) => void;
}

const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function MescreenOrderModal({ patientId, integration, isOpen, onClose, onOrdered }: MescreenOrderModalProps) {
  const kit = integration.products?.[0] ?? null;

  const kitRef = useRef(kit); kitRef.current = kit;
  const onOrderedRef = useRef(onOrdered); onOrderedRef.current = onOrdered;

  const config = useMemo<LabOrderConfig>(() => ({
    vendor: 'mescreen',
    title: 'Order mescreen Test Kit',
    requireAddress: true,
    // Ship-to-patient kit, like TruDiagnostic: identity stays locked to the
    // chart and none of the collection-visit UI applies.
    lockPatientIdentity: true,
    features: {
      presets: false, facilityProvider: false, billing: false, diagnoses: false, tests: false,
      notes: false, collectionDate: false, vitals: false,
    },
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      const k = kitRef.current;
      if (!k) throw new Error('mescreen is not set up for this practice yet.');

      // A lab order may FILL a missing demographic, never change one (see
      // LabOrderModalCore.buildDemographicsFill()).
      if (payload.demographicsFill) {
        await updatePatient(payload.patientId, payload.demographicsFill);
      }

      const result = await createIntegrationOrder(payload.patientId, {
        key: 'mescreen',
        items: [{ integrationProductId: k.integrationProductId, quantity: 1 }],
        metadata: {
          shippingAddress: {
            recipientName: payload.recipientName || undefined,
            line1: payload.street,
            line2: payload.street2 || undefined,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            country: payload.country || undefined,
            phone: payload.phone || undefined,
            patientShippingAddressId: payload.shippingAddressId ?? undefined,
          },
        },
      });
      onOrderedRef.current(result, k.integrationProductId);
      return { ok: true, lines: [`mescreen test kit ordered — Order #${result.integrationOrderId}`] };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const itemsSlot = (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="text-sm font-semibold text-slate-700">Test</span>
      </div>
      <div className="px-4 py-3 text-sm">
        {kit ? (
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-slate-800">{kit.name}</p>
              <p className="text-xs text-slate-500">At-home kit, mailed to the patient. Results appear here once the lab processes the sample.</p>
            </div>
            {kit.price > 0 && <span className="shrink-0 font-semibold text-slate-800">{usd(kit.price)}</span>}
          </div>
        ) : (
          <p className="text-xs text-amber-700">mescreen is not set up for this practice yet.</p>
        )}
      </div>
    </div>
  );

  const confirmSlot = kit ? (
    <div className="space-y-1">
      <p className="font-semibold text-blue-900">Test:</p>
      <p className="text-blue-800">{kit.name}</p>
    </div>
  ) : null;

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={isOpen}
      onClose={onClose}
      onOrdered={() => { /* handled by parent via onOrdered */ }}
      itemsSlot={itemsSlot}
      confirmSlot={confirmSlot}
    />
  );
}
